import { useState, useEffect } from "react";
import { Search, Award, BadgePercent, Clock } from "lucide-react";
import { PageHero, Reveal } from "../components/common";
import { UniversityCard } from "../components/cards";
import { Input } from "../components/ui/input";
import { UNIVERSITIES, DESTINATIONS } from "../mock";
import api from "../lib/api";

export default function Universities() {
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("All");
  const [offerMap, setOfferMap] = useState({});
  const countries = ["All", ...Array.from(new Set(UNIVERSITIES.map((u) => u.country)))];

  useEffect(() => {
    api.get("/universities").then((r) => {
      const m = {};
      (r.data || []).forEach((u) => {
        const n = (u.scholarships || []).length;
        if (n || u.application_fee_waiver || u.early_bird_offer) {
          m[u.slug] = { scholarships: n, waiver: !!u.application_fee_waiver, earlyBird: !!u.early_bird_offer };
        }
      });
      setOfferMap(m);
    }).catch(() => {});
  }, []);

  const list = UNIVERSITIES.filter((u) => {
    const matchQ = u.name.toLowerCase().includes(q.toLowerCase());
    const matchC = country === "All" || u.country === country;
    return matchQ && matchC;
  });

  return (
    <>
      <PageHero
        eyebrow="Our University Network"
        title="Partner universities across the globe"
        subtitle="Discover our partner institutions — including our featured partner, UCSI University — and find the perfect match for your ambitions."
        image="https://images.unsplash.com/photo-1583373834259-46cc92173cb7"
      />
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 -mt-2 mb-12 items-center justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a university..."
                className="pl-11 h-12 rounded-full border-slate-200 focus-visible:ring-[#004030]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {countries.map((c) => (
                <button
                  key={c}
                  onClick={() => setCountry(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    country === c ? "bg-[#004030] text-white" : "bg-[#f0f6f3] text-[#004030] hover:bg-[#e2ede8]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((u, i) => {
              const o = offerMap[u.slug];
              return (
                <Reveal key={u.slug} delay={i * 60}>
                  <div className="relative h-full">
                    {o && (
                      <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5" data-testid={`listing-badges-${u.slug}`}>
                        {o.scholarships > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#013d2b] bg-[#e0c05a] px-2 py-0.5 rounded-full shadow"><Award size={11} /> {o.scholarships} scholarship{o.scholarships > 1 ? "s" : ""}</span>}
                        {o.waiver && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full shadow"><BadgePercent size={11} /> Fee waiver</span>}
                        {o.earlyBird && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-amber-600 px-2 py-0.5 rounded-full shadow"><Clock size={11} /> Early bird</span>}
                      </div>
                    )}
                    <UniversityCard u={u} />
                  </div>
                </Reveal>
              );
            })}
          </div>
          {list.length === 0 && <p className="text-center text-slate-400 py-16">No universities match your filters.</p>}
        </div>
      </section>
    </>
  );
}